import CheckGreen from "check-green.svg";
import cn from "classnames";
import { CustomButtonGroup, NullArrow } from "components/shared/carouselControls";
import Loader from "components/shared/loader";
import Eye from "eye.svg";
import EyeClosed from "eye-closed.svg";
import { ymEvent } from "helpers/system";
import { UmamiManager } from "lib/umami";
import Tooltip from "rc-tooltip";
import React, { useEffect, useState } from "react";
import Lightbox from "react-18-image-lightbox";
import Carousel from "react-multi-carousel";
import { alert } from "services/alert";
import SignalRConnection from "services/signalRConnection";
import { productMetricsEventNames, sendEventLog } from "sources/eventLogs";
import { apply, getRelayTypes, skipSelfRegistrationRelaySetup } from "sources/relays";
import "react-multi-carousel/lib/styles.css";
import "react-18-image-lightbox/style.css";

const certificates = [
    "/images/certificates/certificate-1.jpg",
    "/images/certificates/certificate-2.jpg",
    "/images/certificates/certificate-3.jpg",
    "/images/certificates/certificate-4.jpg",
    "/images/certificates/certificate-5.jpg",
    "/images/certificates/certificate-6.jpg",
    "/images/certificates/certificate-7.jpg",
    "/images/certificates/certificate-8.jpg",
];

const certResponsive = {
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 6 },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 4 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 3 },
};

const clientResponsive = {
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 3 },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 2 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 2 },
};

export default function CreateRelay(props) {
    const [loading, setLoading] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [helpModalOpen, setHelpModalOpen] = useState(false);
    const [relayTypes, setRelayTypes] = useState([]);
    const [selectedRelay, setSelectedRelay] = useState(null);
    const [data, setData] = useState({ id: undefined, host: "", login: "", password: "" });
    const [observeOrgStatus, setObserveOrgStatus] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    useEffect(() => {
        getRelayTypes().then((res) => {
            setRelayTypes(res);
            setLoading(false);
        });

        const connection = new SignalRConnection({
            url: "/signalr/relays",
            actions: [
                {
                    name: "Update",
                    action: () => props.getOrg(),
                },
            ],
            joinActionName: "Join",
            leaveActionName: "Leave",
        });
        return () => {
            connection.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!observeOrgStatus) {
            return;
        }

        const relayInitStatus = props.organization.relay?.initStatus;

        if (!relayInitStatus) {
            return;
        }

        if (relayInitStatus === "Success") {
            setObserveOrgStatus(false);
            setLoading(false);
            props.onConnected?.();
            return;
        }

        if (props.organization.relay?.errorMessage) {
            alert({
                type: "error",
                message: props.organization.relay?.errorMessage,
                title: "Ошибка",
            });
            setObserveOrgStatus(false);
            setLoading(false);
        }
    }, [observeOrgStatus, props.organization]);

    useEffect(() => {
        if (!helpModalOpen) {
            return;
        }

        const closeOnEscape = (e) => {
            if (e.key === "Escape") {
                setHelpModalOpen(false);
            }
        };

        document.addEventListener("keydown", closeOnEscape);

        return () => document.removeEventListener("keydown", closeOnEscape);
    }, [helpModalOpen]);

    const createRelay = (e) => {
        e.preventDefault();

        ymEvent("ButtonConnectTheSystemClicked");
        if (props.connectionSource === "banner") {
            ymEvent("RelayConnectionBannerConnectSystemClicked");
        }
        UmamiManager.track("ConnectSystemButtonClicked");

        setLoading(true);

        apply(data).then((res) => {
            if (res) {
                setObserveOrgStatus(true);
                alert({
                    type: "success",
                    message: "Выполняется подключение к системе мониторинга и получение данных",
                    title: "Немного подождите...",
                });
            } else {
                setLoading(false);
            }
        });
    };

    const selectRelay = (id) => {
        const selected = relayTypes.find((item) => item.id === Number(id));
        setSelectedRelay(selected);
        setData({
            ...data,
            id: Number(id),
            host: "",
            login: "",
            password: "",
            ...(selected.defaultValues ?? {}),
        });
    };

    const openLightbox = (index) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    const skipRelaySetup = () => {
        ymEvent("RelayConnectionSkipLaterClicked");
        setLoading(true);

        skipSelfRegistrationRelaySetup()
            .then((res) => {
                if (res) {
                    sendEventLog({
                        name: productMetricsEventNames.ContinueWithoutCars,
                        payload: { source: "self_registration_relay_setup" },
                    });
                    props.getOrg();
                    props.onSkip?.();
                } else {
                    setLoading(false);
                }
            })
            .catch(() => setLoading(false));
    };

    const openTechSupportChat = () => {
        setHelpModalOpen(false);
        props.onTechSupportOpen?.();
    };

    return (
        <>
            {loading && (
                <div className="loader-snippet--disable-loader">
                    <Loader />
                </div>
            )}
            {lightboxOpen && (
                <Lightbox
                    mainSrc={certificates[lightboxIndex]}
                    nextSrc={certificates[(lightboxIndex + 1) % certificates.length]}
                    prevSrc={certificates[(lightboxIndex + certificates.length - 1) % certificates.length]}
                    onCloseRequest={() => setLightboxOpen(false)}
                    onMovePrevRequest={() =>
                        setLightboxIndex((lightboxIndex + certificates.length - 1) % certificates.length)
                    }
                    onMoveNextRequest={() => setLightboxIndex((lightboxIndex + 1) % certificates.length)}
                />
            )}
            <div
                className={cn("registration-panel__wrap registration-panel__wrap--relay", {
                    "registration-panel__wrap--relay-modal": props.modalMode,
                })}
            >
                <div className="r-relay__left">
                    {props.beforeContainer}
                    {!props.modalMode && (
                        <>
                            <div className="registration-panel__title">
                                Поздравляем аккаунт создан!&nbsp;
                                <span className="font-size-52">
                                    <CheckGreen />
                                </span>
                            </div>
                            <div className="registration-panel__subtitle">
                                Теперь подключим вашу систему мониторинга, <br />
                                чтобы видеть машины на карте
                            </div>
                        </>
                    )}
                    <div className="registration-panel__container">
                        <div className="registration-panel registration-panel--small">
                            <form className="d-flex flex-column w-100" autoComplete="off" onSubmit={createRelay}>
                                <div className="registration-panel__inner--title">
                                    Интеграция с системой мониторинга
                                </div>
                                <div className="flex-grow-1 overflow-auto">
                                    <div className="registration-panel__inner">
                                        <div className={cn("r-form__group", { "r-form__group--full": data.id })}>
                                            <div>
                                                <label htmlFor="type" className="r-form__label">
                                                    Система мониторинга&nbsp;
                                                    <Tooltip overlay="После подключения вы увидите машины на карте и их движение">
                                                        <i className="icon-uEA69-question-circle text-black-50" />
                                                    </Tooltip>
                                                </label>
                                                <select
                                                    required
                                                    className="r-form__input r-form__select"
                                                    value={selectedRelay?.id || ""}
                                                    id="type"
                                                    autoComplete="off"
                                                    name="type"
                                                    onChange={(e) => selectRelay(e.target.value)}
                                                >
                                                    <option disabled value="">
                                                        Выберите из списка
                                                    </option>
                                                    {relayTypes.map((item) => (
                                                        <option key={item.id} value={item.id}>
                                                            {item.displayName}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="r-form__after-icon">{data.id && <CheckGreen />}</div>
                                        </div>
                                        {selectedRelay?.fields?.includes("Host") && (
                                            <div className="r-form__group r-form__group--full">
                                                <div>
                                                    <label htmlFor="host" className="r-form__label">
                                                        Адрес системы мониторинга&nbsp;
                                                        <Tooltip
                                                            overlayClassName="mw-350"
                                                            overlay="Ссылка на вашу систему мониторинга можно скопировать из адресной строки браузера, посмотреть в договоре или узнайте у поставщика системы мониторинга"
                                                        >
                                                            <i className="icon-uEA69-question-circle text-black-50" />
                                                        </Tooltip>
                                                    </label>
                                                    <input
                                                        {...(selectedRelay?.requiredFields?.includes("Host")
                                                            ? { required: true }
                                                            : {})}
                                                        className="r-form__input"
                                                        value={data.host}
                                                        id="host"
                                                        autoComplete="off"
                                                        name="host"
                                                        onChange={(e) => setData({ ...data, host: e.target.value })}
                                                        placeholder="gps.example.ru"
                                                    />
                                                </div>
                                                <div className="r-form__after-icon">
                                                    {data.host.length > 0 && <CheckGreen />}
                                                </div>
                                            </div>
                                        )}
                                        {selectedRelay?.fields?.includes("Login") && (
                                            <div
                                                className={cn("r-form__group", {
                                                    "r-form__group--full": data.login.length > 0,
                                                })}
                                            >
                                                <div>
                                                    <label htmlFor="login" className="r-form__label">
                                                        Логин системы мониторинга&nbsp;
                                                        <Tooltip overlay="Логин, который используется для входа в систему мониторинга">
                                                            <i className="icon-uEA69-question-circle text-black-50" />
                                                        </Tooltip>
                                                    </label>
                                                    <input
                                                        {...(selectedRelay?.requiredFields?.includes("Login")
                                                            ? { required: true }
                                                            : {})}
                                                        className="r-form__input"
                                                        value={data.login}
                                                        id="login"
                                                        name="login"
                                                        autoComplete="off"
                                                        onChange={(e) => setData({ ...data, login: e.target.value })}
                                                        placeholder="Введите логин"
                                                    />
                                                </div>
                                                <div className="r-form__after-icon">
                                                    {data.login.length > 0 && <CheckGreen />}
                                                </div>
                                            </div>
                                        )}
                                        {selectedRelay?.fields?.includes("Password") && (
                                            <div
                                                className={cn("r-form__group", {
                                                    "r-form__group--full": data.password.length > 0,
                                                })}
                                            >
                                                <div className="position-relative">
                                                    <label htmlFor="password" className="r-form__label">
                                                        Пароль системы мониторинга&nbsp;
                                                        <Tooltip overlay="Пароль, который используется для входа в систему мониторинга">
                                                            <i className="icon-uEA69-question-circle text-black-50" />
                                                        </Tooltip>
                                                    </label>
                                                    <input
                                                        {...(selectedRelay?.requiredFields?.includes("Password")
                                                            ? { required: true }
                                                            : {})}
                                                        className="r-form__input"
                                                        autoComplete="new-password"
                                                        type={showPassword ? "text" : "password"}
                                                        value={data.password}
                                                        id="password"
                                                        name="password"
                                                        onChange={(e) => setData({ ...data, password: e.target.value })}
                                                        placeholder="Введите пароль"
                                                    />
                                                    <span
                                                        className="r-form__input-inline-btn"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                    >
                                                        {showPassword ? <EyeClosed /> : <Eye />}
                                                    </span>
                                                </div>
                                                <div className="r-form__after-icon">
                                                    {data.password.length > 0 && <CheckGreen />}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="registration-panel__inner">
                                    <div className="mt-3 mb-2 text-center">
                                        <button className="r-form__button" type="submit">
                                            Подключить систему
                                        </button>
                                    </div>
                                    {!props.modalMode && (
                                        <div className="r-relay__help-link-wrap">
                                            <button
                                                className="r-relay__help-link"
                                                type="button"
                                                onClick={() => setHelpModalOpen(true)}
                                            >
                                                Не получается подключить?
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                    {!props.modalMode && helpModalOpen && (
                        <>
                            <div className="r-relay-help__backdrop" onClick={() => setHelpModalOpen(false)} />
                            <div
                                className="r-relay-help"
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby="relay-help-title"
                            >
                                <button
                                    className="r-relay-help__close"
                                    type="button"
                                    aria-label="Закрыть"
                                    onClick={() => setHelpModalOpen(false)}
                                >
                                    ×
                                </button>
                                <div className="r-relay-help__title" id="relay-help-title">
                                    Нужна помощь с подключением?
                                </div>
                                <div className="r-relay-help__text">
                                    Если у вас нет данных для подключения или вы не знаете, какую систему выбрать, мы
                                    поможем настроить мониторинг.
                                </div>
                                <button
                                    className="r-relay-help__support-button"
                                    type="button"
                                    onClick={openTechSupportChat}
                                >
                                    <i className="icon-uEA99-chat" aria-hidden="true" />
                                    <span>Получить помощь</span>
                                </button>
                                <button className="r-relay-help__skip-button" type="button" onClick={skipRelaySetup}>
                                    Продолжить без отображения машин на карте
                                </button>
                                <div className="r-relay-help__warning">
                                    <i className="icon-uEA86-exclamation-circle" aria-hidden="true" />
                                    <span>Без подключения мониторинга машины не будут отображаться на карте.</span>
                                </div>
                            </div>
                        </>
                    )}
                    {props.afterContainer}
                    {!props.modalMode && (
                        <div className="r-steps">
                            <div className="r-steps__item r-steps__item--active">
                                <div className="r-steps__item-circle">1</div>
                                <div className="r-steps__item-text">ШАГ</div>
                            </div>
                            <div className="r-steps__line r-steps__line--active" />
                            <div className="r-steps__item r-steps__item--active">
                                <div className="r-steps__item-circle">2</div>
                                <div className="r-steps__item-text">ШАГ</div>
                            </div>
                            <div className="r-steps__line r-steps__line--active" />
                            <div className="r-steps__item r-steps__item--active">
                                <div className="r-steps__item-circle">3</div>
                                <div className="r-steps__item-text">ШАГ</div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="r-relay__right">
                    <div className="r-relay__right--inner">
                        <div className="r-relay__security-title">Сертификаты и защита данных</div>
                        <div className="r-relay__security-text">
                            Подключение безопасно.
                            <br />
                            Мы получаем только данные для отображения.
                            <br />
                            Не управляем и не изменяем вашу систему.
                        </div>
                        <div className="r-relay__certificates">
                            <Carousel
                                responsive={certResponsive}
                                infinite
                                renderButtonGroupOutside
                                customLeftArrow={<NullArrow />}
                                customRightArrow={<NullArrow />}
                                customButtonGroup={<CustomButtonGroup />}
                            >
                                {certificates.map((src, i) => (
                                    <div key={i} className="text-center px-1" onClick={() => openLightbox(i)}>
                                        <img src={src} alt={`Сертификат ${i + 1}`} className="r-relay__cert-img" />
                                    </div>
                                ))}
                            </Carousel>
                        </div>
                        <div className="r-relay__clients-title text-center">
                            Нам доверяют владельцы более <span className="font-weight-bolder">16 000</span> транспортных
                            средств
                        </div>
                        <div className="r-relay__certificates">
                            <Carousel
                                responsive={clientResponsive}
                                infinite
                                renderButtonGroupOutside
                                customLeftArrow={<NullArrow />}
                                customRightArrow={<NullArrow />}
                                customButtonGroup={<CustomButtonGroup />}
                            >
                                {[
                                    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33,
                                    34,
                                ].map((n) => (
                                    <div key={n} className="text-center px-1">
                                        <img
                                            className="r-clients__item"
                                            src={`/images/client-logos/logo-${n}.jpg`}
                                            alt=""
                                        />
                                    </div>
                                ))}
                            </Carousel>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
